import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import QuestionRenderer from "../QuestionRenderer";
import { loadAnswers, saveAnswer, submitAnswers, clearAnswers } from "../../utils/SurveyAnswers";
import { getSurvey } from "../../utils/APIConnector";
import { downloadSurveyResponse, ensureLoginWithRid } from "../../utils/APIConnector/APIConnector";
import { clearToken } from "../../session/TokenStore";
import styles from "./SurveyPage.module.css";

function isValidAnswer(question, value) {
    const qt = question?.questionType;
    const details = question?.details || {};
    const answerType = details?.answerType;

    const nonEmptyString = (v) => typeof v === "string" && v.trim().length > 0;
    const isFiniteNumber = (v) => typeof v === "number" && Number.isFinite(v);
    const getIntBounds = (d) => {
        const min = Number.isFinite(d.min) ? d.min : 0;
        const max = Number.isFinite(d.max) ? d.max : Infinity;
        return { min, max };
    };

    if (qt === "Ladder") {
        const choice = value?.choice ?? null;
        const d = (value?.details && typeof value.details === "object") ? value.details : {};
        if (d.other || d.skip) return true;
        const min = Number.isFinite(details.min) ? details.min : 1;
        const max = Number.isFinite(details.max) ? details.max : 10;
        return isFiniteNumber(choice) && choice >= min && choice <= max;
    }
    if (qt === "SC") {
        const choice = value?.choice ?? null;
        return isFiniteNumber(choice) && choice > 0;
    }
    if (qt === "MC") {
        const arr = Array.isArray(value?.choice) ? value.choice : (Array.isArray(value) ? value : []);
        return arr.length > 0;
    }
    if (qt === "Open") {
        const hasExtras = Array.isArray(details.options) && details.options.length > 0;
        if (hasExtras) {
            const d = (value?.details && typeof value.details === "object") ? value.details : {};
            const anyChecked = Array.isArray(d.selected) && d.selected.length > 0;
            if (anyChecked) return true; // selecting an extra option is a valid answer
        }
        const v = value?.choice ?? value;
        if (answerType === "int") {
            if (v === "" || v === null || v === undefined) return false;
            const n = Number(v);
            const { min, max } = getIntBounds(details);
            return Number.isFinite(n) && n >= min && n <= max;
        }
        return nonEmptyString(typeof v === "string" ? v : String(v ?? ""));
    }
    if (qt === "OpenList") {
        const isInt = answerType === "int";
        const arr = Array.isArray(value?.choice) ? value.choice : [];
        const d = (value?.details && typeof value.details === "object") ? value.details : {};
        const hasExtras = Array.isArray(details.options) && details.options.length > 0;
        const anyChecked = Array.isArray(d.selected) && d.selected.length > 0;
        if (hasExtras && anyChecked) return true;
        if (!arr.length) return false;
        if (isInt) {
            const filtered = arr.filter((v) => v !== "" && v !== null && v !== undefined);
            if (!filtered.length) return false;
            const { min, max } = getIntBounds(details);
            return filtered.every((v) => {
                const n = Number(v);
                return Number.isFinite(n) && n >= min && n <= max;
            });
        }
        return arr.some((s) => typeof s === "string" ? s.trim().length > 0 : (s != null && String(s).trim().length > 0));
    }
    if (qt === "SelectText") {
        const choice = value?.choice;
        const text = value?.details?.text ?? "";
        const hasChoice = typeof choice === 'number' && choice > 0;
        // const hasText = typeof text === 'string' && text.trim().length > 0;
        const hasText = true;
        return hasChoice && hasText;
    }
    if (qt === "Label") {
        // Informational-only question; never blocks navigation
        return true;
    }
}

function errorMessage(question, t) {
    const qt = question.questionType;
    const answerType = question.details?.answerType;
    const details = question.details || {};
    
    if (qt === "Ladder") return t("survey.error.select_ladder", { defaultValue: "Wybierz szczebel" });
    if (qt === "SC") return t("survey.error.select_one");
    if (qt === "MC") return t("survey.error.select_multiple");
    if (qt === "Open") {
        if (answerType === "int") {
            const { min, max } = { min: Number.isFinite(details.min) ? details.min : 0, max: Number.isFinite(details.max) ? details.max : Infinity };
            const maxStr = max === Infinity ? "∞" : max;
            return t("survey.error.enter_int_range", { defaultValue: `Wprowadź liczbę między ${min} a ${maxStr}`, min, max: maxStr });
        }
        return t("survey.error.enter_answer");
    }
    if (qt === "OpenList") {
        if (answerType === "int") {
            const { min, max } = { min: Number.isFinite(details.min) ? details.min : 0, max: Number.isFinite(details.max) ? details.max : Infinity };
            const maxStr = max === Infinity ? "∞" : max;
            return t("survey.error.enter_int_range", { defaultValue: `Wprowadź liczbę między ${min} a ${maxStr}`, min, max: maxStr });
        }
        return t("survey.error.enter_answer");
    }
    if (qt === "SelectText") return t("survey.error.select_and_enter", { defaultValue: "Wybierz opcję i wpisz tekst" });
    return t("survey.error.required");
}


function normalizeAnswerValue(answerVal) {
    if (answerVal && typeof answerVal === "object") {
        if ("choice" in answerVal || "details" in answerVal) {
            return answerVal;
        }
        if (Array.isArray(answerVal)) {
            return { choice: answerVal };
        }
        const val = answerVal.value ?? answerVal.answer ?? answerVal;
        return { choice: val };
    }
    return { choice: answerVal };
}

function computeMaxPageFromAnswers(byQid, questionnaire) {
    if (!Array.isArray(questionnaire) || !questionnaire.length) return 1;
    const pageAnswered = new Set();
    for (const q of questionnaire) {
        const isMetric = !!q.isMetric || (Number(q.questionID) > 1000);
        if (isMetric) continue;
        const ans = byQid?.[q.questionID]?.value;
        if (ans == null) continue;
        if (isValidAnswer(q, ans)) pageAnswered.add(Number(q.pageNumber || 1));
    }
    const maxInData = pageAnswered.size ? Math.max(...pageAnswered) : 1;
    const total = Math.max(...questionnaire.map((q) => Number(q.pageNumber || 1)));
    return Math.min(Math.max(1, maxInData), total);
}


export default function SurveyPage() {
    const { id, page } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { t, i18n } = useTranslation();

    const pageNum = Number(page) > 0 ? Number(page) : 1;

    const [survey, setSurvey] = useState(null);
    const [error, setError] = useState(false);
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [showValidation, setShowValidation] = useState(false);
    const [hasBack, setHasBack] = useState(false);
    // Refs to question containers by qid to enable scrolling on validation failure
    const questionRefs = useRef({});

    // Ensure scroll to top works reliably across browsers and layouts
    const scrollToTop = () => {
        const doScroll = () => {
            try {
                window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
            } catch {
                window.scrollTo(0, 0);
            }
            try { document.body.scrollTop = 0; } catch { }
            try { document.documentElement.scrollTop = 0; } catch { }
        };
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(doScroll); else setTimeout(doScroll, 0);
    };

    useEffect(() => {
        let alive = true;
        setError(false);
        setSurvey(null);
        (async () => {
            try {
                const raw = await getSurvey(id);
                if (alive) setSurvey(raw);
                // initialize canonical currentPage if not present
                try {
                    const curKey = `survey:${id}:currentPage`;
                    if (!sessionStorage.getItem(curKey)) {
                        sessionStorage.setItem(curKey, '1');
                    }
                } catch { }
            } catch {
                if (alive) setError(true);
            }
        })();

    }, [id]);
    useEffect(() => { setShowValidation(false); scrollToTop(); }, [id, pageNum]);

    // Guard: only allow page changes through app controls (Next/Back) by enforcing a canonical currentPage in sessionStorage
    useEffect(() => {
        const curKey = `survey:${id}:currentPage`;
        const maxKey = `survey:${id}:maxPage`;
        let cur = Number(sessionStorage.getItem(curKey) || '0');
        let max = Math.max(1, Number(sessionStorage.getItem(maxKey) || '1'));
        if (!cur || cur < 1) {
            cur = 1;
            try { sessionStorage.setItem(curKey, String(cur)); } catch { }
        }
        // Clamp current to max (cannot jump ahead beyond what has been unlocked)
        if (cur > max) {
            cur = max;
            try { sessionStorage.setItem(curKey, String(cur)); } catch { }
        }
        if (pageNum !== cur) {
            navigate(`/surveys/${id}/page/${cur}`, { replace: true });
            setTimeout(scrollToTop, 0);
        }
    }, [id, pageNum, navigate]);

    // History stack helpers
    const historyKey = `survey:${id}:historyStack`;
    const readHistory = () => {
        try {
            const raw = sessionStorage.getItem(historyKey);
            const arr = JSON.parse(raw || '[]');
            return Array.isArray(arr) ? arr : [];
        } catch { return []; }
    };
    const writeHistory = (arr) => {
        try { sessionStorage.setItem(historyKey, JSON.stringify(arr)); } catch { }
    };
    const pushHistory = (p) => {
        const arr = readHistory();
        // Avoid duplicate consecutive entries
        if (arr[arr.length - 1] !== p) {
            arr.push(p);
            writeHistory(arr);
        }
    };
    const popHistory = () => {
        const arr = readHistory();
        const prev = arr.pop();
        writeHistory(arr);
        return prev ?? null;
    };

    // Update hasBack whenever page changes
    useEffect(() => {
        setHasBack(readHistory().length > 0);
    }, [id, pageNum]);
    const langSection = useMemo(() => {
        if (!survey) return { questionnaire: [] };
        const lang = i18n.language;
        const hasLang =
            survey[lang] &&
            Array.isArray(survey[lang].questionnaire) &&
            survey[lang].questionnaire.length > 0;
        const hasEn =
            survey?.en &&
            Array.isArray(survey.en.questionnaire) &&
            survey.en.questionnaire.length > 0;
        const hasPl =
            survey?.pl &&
            Array.isArray(survey.pl.questionnaire) &&
            survey.pl.questionnaire.length > 0;
        if (hasLang) return survey[lang];
        if (hasEn) return survey.en;
        if (hasPl) return survey.pl;
        const any = Object.values(survey).find(
            (v) =>
                v &&
                typeof v === "object" &&
                Array.isArray(v.questionnaire) &&
                v.questionnaire.length > 0
        );
        return any || { questionnaire: [] };
    }, [survey, i18n.language]);

    useEffect(() => { setAnswers(loadAnswers(id)); }, [id]);

    // Compute questionnaire and dependent values early for use by effects
    const questionnaire = useMemo(() => langSection.questionnaire || [], [langSection]);
    const questionsOnPage = useMemo(
        () => questionnaire.filter((q) => q.pageNumber === pageNum),
        [questionnaire, pageNum]
    );
    const totalPages = useMemo(() => {
        if (!questionnaire.length) return 1;
        return Math.max(...questionnaire.map((q) => q.pageNumber));
    }, [questionnaire]);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const rid = sessionStorage.getItem(`survey:${id}:rid`);
                if (!rid) return;

                const data = await downloadSurveyResponse(rid);
                const byQid = {};

                // Preferred shape (as per your JSON): data.answers is an object keyed by qid
                if (data && typeof data === 'object' && data.answers && typeof data.answers === 'object') {
                    for (const [k, obj] of Object.entries(data.answers)) {
                        const qid = Number(k);
                        if (!Number.isFinite(qid) || qid <= 1000) continue; // only indices/metrics
                        const rawVal = obj?.value ?? obj;
                        const normalized = normalizeAnswerValue(rawVal);
                        const pg = Number(obj?.page) > 0 ? Number(obj.page) : 1;
                        byQid[qid] = { value: normalized, page: pg };
                    }
                } else if (Array.isArray(data?.answers)) {
                    // Alternative shape: answers is an array
                    for (const item of data.answers) {
                        const qid = Number(item?.questionID ?? item?.questionId ?? item?.qid ?? item?.id);
                        if (!Number.isFinite(qid) || qid <= 1000) continue;
                        const rawVal = item?.answer ?? item?.value ?? item;
                        const normalized = normalizeAnswerValue(rawVal);
                        const pg = Number(item?.page) > 0 ? Number(item.page) : 1;
                        byQid[qid] = { value: normalized, page: pg };
                    }
                } else if (Array.isArray(data)) {
                    // Fallback: top-level array of answers
                    for (const item of data) {
                        const qid = Number(item?.questionID ?? item?.questionId ?? item?.qid ?? item?.id);
                        if (!Number.isFinite(qid) || qid <= 1000) continue;
                        const rawVal = item?.answer ?? item?.value ?? item;
                        const normalized = normalizeAnswerValue(rawVal);
                        const pg = Number(item?.page) > 0 ? Number(item.page) : 1;
                        byQid[qid] = { value: normalized, page: pg };
                    }
                }

                if (Object.keys(byQid).length) {
                    try {
                        // Merge with any existing answers rather than overwrite
                        const existing = JSON.parse(sessionStorage.getItem(`survey:${id}:answers`) || '{}');
                        const merged = { ...existing, ...byQid };
                        sessionStorage.setItem(`survey:${id}:answers`, JSON.stringify(merged));
                        sessionStorage.setItem(`survey:${id}:ridApplied:${rid}`, '1');
                        // After merging answers, update currentPage to max answered page (but not beyond maxPage)
                        const maxAnswered = computeMaxPageFromAnswers(merged, questionnaire);
                        const maxKey = `survey:${id}:maxPage`;
                        const curKey = `survey:${id}:currentPage`;
                        const maxAllowed = Math.max(1, Number(sessionStorage.getItem(maxKey) || '1'));
                        const nextCur = Math.min(maxAnswered, maxAllowed);
                        sessionStorage.setItem(curKey, String(nextCur || 1));
                        if (alive) setAnswers(merged);
                    } catch {
                        if (alive) setAnswers(byQid);
                    }
                }
            } catch (e) {
                console.error("Błąd podczas pobierania odpowiedzi ankiety:", e);
                // If fetching answers failed (e.g., wrong RID entered), clear stored RID
                try {
                    const badRid = sessionStorage.getItem(`survey:${id}:rid`);
                    if (badRid) {
                        sessionStorage.removeItem(`survey:${id}:rid`);
                        sessionStorage.removeItem(`survey:${id}:ridApplied:${badRid}`);
                    }
                } catch { }
            }
        })();
        return () => { alive = false; };
    }, [id]);

    useEffect(() => {
        const key = `survey:${id}:maxPage`;
        const maxPage = Math.max(1, Number(sessionStorage.getItem(key) || "1"));
        if (pageNum > maxPage) {
            navigate(`/surveys/${id}/page/${maxPage}`, { replace: true });
            setTimeout(scrollToTop, 0);
        }
    }, [id, pageNum, navigate]);

    useEffect(() => { setShowValidation(false); scrollToTop(); }, [id, pageNum]);

    // (moved earlier to avoid temporal dead zone in effects)

    const pageValid = useMemo(() => {
        return questionsOnPage.every((q) => {
            const v = answers?.[q.questionID]?.value;
            return isValidAnswer(q, v);
        });
    }, [questionsOnPage, answers]);

    if (error) return <p>{t("survey.loadError")}</p>;
    if (!survey) return <p>{t("survey.loading")}</p>;

    const getSkipLogic = (questions) => {
        const logic = {};
        let questionID = null;
        questions.forEach((q) => {
            const hasSkip = q?.details?.skipLogic && typeof q.details.skipLogic === 'object';
            if (!hasSkip) return;
            if (q.questionType === "SC" || q.questionType === "closed question SC") {
                Object.entries(q.details.skipLogic).forEach(([key, value]) => {
                    questionID = q.questionID;
                    logic[key] = value;
                });
            }
        });
        return { questionID, ...logic };
    };

    const goNext = async () => {
        setShowValidation(true);
        if (!pageValid) {
            // Find first invalid question in visual order and scroll to it
            for (const q of questionsOnPage) {
                const qid = q.questionID;
                const v = answers?.[qid]?.value;
                if (!isValidAnswer(q, v)) {
                    const el = questionRefs.current?.[qid];
                    if (el && typeof el.scrollIntoView === 'function') {
                        try { el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' }); } catch { window.scrollTo(0, el.getBoundingClientRect?.().top || 0); }
                    }
                    // Try to focus the first focusable control inside
                    try {
                        const focusable = el?.querySelector?.('input, select, textarea, button, [tabindex]:not([tabindex="-1"])');
                        focusable?.focus?.();
                    } catch { }
                    break;
                }
            }
            return;
        }

        const key = `survey:${id}:maxPage`;
        const curKey = `survey:${id}:currentPage`;
        const currentMax = Math.max(1, Number(sessionStorage.getItem(key) || "1"));
        const skipLogic = getSkipLogic(questionsOnPage);
        const currentAnswers = loadAnswers(id);
        const rawVal = currentAnswers?.[skipLogic.questionID]?.value;
        // Support both legacy raw values and new {choice} shapes
        const keyVal = (rawVal && typeof rawVal === 'object' && rawVal.choice != null) ? rawVal.choice : rawVal;
        const jumpTo = skipLogic?.[String(keyVal)] || 0;
        const nextPage = jumpTo > pageNum + 1 ? jumpTo : pageNum + 1;
        sessionStorage.setItem(key, String(Math.max(currentMax, nextPage)));
        // set canonical currentPage before navigation
        try { sessionStorage.setItem(curKey, String(nextPage)); } catch { }
        // push current page onto history stack for back navigation
        pushHistory(pageNum);

        if (pageNum < totalPages) {
            navigate(`/surveys/${id}/page/${nextPage}`);
            setTimeout(scrollToTop, 0);
            return;
        }

        try {
            setSubmitting(true);
            setSubmitError("");

            // Determine participantID: use pasted RID (URL or sessionStorage), else 'anonymous'
            const qs = new URLSearchParams(location.search);
            let pastedRid = (qs.get("rid") || "").trim();
            if (!pastedRid) {
                try { pastedRid = (sessionStorage.getItem(`survey:${id}:rid`) || "").trim(); } catch { }
            }
            const participantIdToSend = pastedRid || "anonymous";

            // Submit answers with participantID
            const res = await submitAnswers({ id, survey, answers, language: i18n.language, participantID: participantIdToSend });

            // Read response id from backend
            let responseId = res?.id || res?.responseId || res?.uuid || null;

            // Determine RID to show on finish:
            // 1) If user pasted a RID, prefer it
            // 2) Else if backend returned responseId, treat it as new RID (ensure login, store temporarily)
            let ridToUse = pastedRid || null;
            if (!ridToUse && responseId) {
                try {
                    await ensureLoginWithRid(responseId);
                    ridToUse = responseId;
                    try { sessionStorage.setItem(`survey:${id}:rid`, ridToUse); } catch { }
                } catch { }
            }

            // Clear all stored data so a new run without RID won't prefill or stay logged in
            clearAnswers(id);
            try { sessionStorage.removeItem(`survey:${id}:rid`); } catch { }
            try { sessionStorage.removeItem(`survey:${id}:ridApplied:${ridToUse || responseId}`); } catch { }
            try { sessionStorage.removeItem(`survey:${id}:visitedPages`); } catch { }
            try { sessionStorage.removeItem(`survey:${id}:historyStack`); } catch { }
            try { sessionStorage.removeItem(`survey:${id}:maxPage`); } catch { }
            try { sessionStorage.removeItem(`survey:${id}:currentPage`); } catch { }
            try { clearToken(); } catch { }

            // Navigate to finish, passing RID only via navigation state (not persisted)
            navigate(`/surveys/${id}/finish`, { replace: true, state: { rid: ridToUse || responseId } });
            setTimeout(scrollToTop, 0);
        } catch (e) {
            setSubmitError(e?.message || t("survey.submitError"));
        } finally {
            setSubmitting(false);
        }
    };

    const goBack = () => {
        const curKey = `survey:${id}:currentPage`;
        const prev = popHistory();
        if (prev && prev !== pageNum) {
            try { sessionStorage.setItem(curKey, String(prev)); } catch { }
            navigate(`/surveys/${id}/page/${prev}`);
            setTimeout(scrollToTop, 0);
        }
        // if no history, do nothing
    };

    return (
        <div className={styles.container}>
            {/* Progress bar representing current page over total pages */}
            <div
                className={styles.progressWrap}
                role="progressbar"
                aria-label={t('survey.pageProgress', { defaultValue: 'Postęp ankiety' })}
                aria-valuemin={0}
                aria-valuemax={totalPages}
                aria-valuenow={pageNum}
            >
                <div className={styles.progressBar}>
                    <div
                        className={styles.progressFill}
                        style={{ width: `${Math.max(0, Math.min(100, (totalPages ? (pageNum / totalPages) : 0) * 100))}%` }}
                    />
                </div>
            </div>

            <div className={styles.questions}>
                {questionsOnPage.map((q) => {
                    const qid = q.questionID;
                    const value = answers?.[qid]?.value ?? (q.questionType === "multiple" ? [] : undefined);
                    const invalid = showValidation && !isValidAnswer(q, value);

                    return (
                        <div
                            key={qid}
                            ref={(el) => {
                                if (!questionRefs.current) questionRefs.current = {};
                                if (el) questionRefs.current[qid] = el; else delete questionRefs.current[qid];
                            }}
                            className={`${styles.question} ${invalid ? styles.invalid : ""}`}
                        >
                            <div className={styles.qcontent}>{q.questionContent}</div>

                            <QuestionRenderer
                                q={q}
                                name={`q-${qid}`}
                                value={value}
                                onChange={(val) => {
                                    saveAnswer(id, qid, val, pageNum);
                                    setAnswers(loadAnswers(id));
                                }}
                            />

                            {showValidation && invalid && (
                                <div className={styles.errorText}>{errorMessage(q, t)}</div>
                            )}
                        </div>
                    );
                })}
            </div>

            {submitError && <div className={styles.errorBox}>{submitError}</div>}

            <div className={styles.footer}>
                <button className={styles.prevBtn} onClick={goBack} disabled={submitting || !hasBack}>
                    {t("survey.actions.back", { defaultValue: "Wstecz" })}
                </button>
                <button className={styles.nextBtn} onClick={goNext} disabled={submitting}>
                    {pageNum < totalPages ? t("survey.actions.next") : (submitting ? t("survey.actions.submitting") : t("survey.actions.finish"))}
                </button>
            </div>
        </div>
    );
}
